/**
 * CPU プレイヤーのチューニング設定（プレイヤー個別 + localStorage オーバーレイ）。
 *
 * 設計:
 *   - 組込デフォルト ({@link STYLE_DEFAULTS}) は **スタイル単位**（'tricky' / 'safe'）に定義。
 *     新しい CPU プレイヤーを追加した直後はそのスタイルのデフォルト値で動作する。
 *   - 永続化オーバーレイは **CPU プレイヤー単位（cpuId キー）** で localStorage に保存する
 *     （`pool.cpu.config.v2`）。これにより Efren と Intermediate を**独立して**チューニングできる。
 *   - {@link getCpuConfig} はデフォルト（スタイル単位）と overlay（cpuId 単位）をマージして返す
 *     （プロセス内キャッシュ・save/reset 時に無効化）。
 *   - {@link STYLE_CONFIG_META} は管理 UI のフォーム自動生成に使う（フィールド種別・範囲・ラベル）。
 *
 * フォールバック互換:
 *   旧 v1 キー（`pool.cpu.config.v1`・スタイル単位）が残っている場合は無視（同じユーザーが
 *   再設定する想定・データ移行は不要と判断）。新規環境では v2 のみ参照される。
 *
 * 循環依存回避:
 *   本ファイルは cpu-players.ts に値依存しない（CpuStyle 型のみ import type）。CPU プレイヤー
 *   定義から style を引く処理は呼び出し側（main.ts / controls.ts）で実施し、本ファイルへは
 *   cpuId と style 両方を渡す。
 */

import type { CpuStyle } from './cpu-players.js';
import { readJson, writeJson } from '../state/storage.js';

// ============================================================
// 型
// ============================================================

export interface StyleConfig {
  /** キックショット候補を生成するか。 */
  readonly enableKicks: boolean;
  /** ポット候補に採用するカット角の上限（rad）。 */
  readonly cutAngleHardLimit: number;
  /** 遠距離セーフティ強制の距離閾値 [m]。Infinity で無効。 */
  readonly farDistanceThreshold: number;
  /** 上の遠距離判定でのカット角閾値（rad）。 */
  readonly farCutAngleLimit: number;
  /** パワー候補 [m/s]。 */
  readonly powerCandidates: readonly number[];
  /** スピン Y 候補（-=引き / 0=ストップ / +=押し）。 */
  readonly spinYCandidates: readonly number[];
  /** 位置スコアの重み。 */
  readonly positionScoreWeight: number;
  /** セーフティ用パワー候補 [m/s]。 */
  readonly safetyPowers: readonly number[];
  /** 「最若球から手玉を遠ざける/物陰に隠す」評価ボーナスを使うか。 */
  readonly enableHidingSafety: boolean;
  /** キック軌道（手玉が的玉接触前にレールに触れる）を完全禁止するか。 */
  readonly forbidPreRailContact: boolean;
  /** カロムポット（球同士をぶつけて入れる）を強く禁止するか。 */
  readonly forbidCaromPots: boolean;
}

// ============================================================
// 組込デフォルト（スタイル単位・不変）
// ============================================================

export const STYLE_DEFAULTS: Readonly<Record<CpuStyle, StyleConfig>> = {
  tricky: {
    enableKicks: true,
    cutAngleHardLimit: Math.PI / 2,
    farDistanceThreshold: Infinity,
    farCutAngleLimit: Math.PI / 2,
    powerCandidates: [2.2, 3.5, 5.0],
    spinYCandidates: [-0.5, -0.3, 0, 0.3],
    positionScoreWeight: 1.0,
    safetyPowers: [0.8, 1.5, 2.5],
    enableHidingSafety: false,
    forbidPreRailContact: false,
    forbidCaromPots: false,
  },
  safe: {
    enableKicks: true,
    cutAngleHardLimit: Math.PI / 2,
    farDistanceThreshold: Infinity,
    farCutAngleLimit: Math.PI / 2,
    powerCandidates: [1.0, 1.6, 2.4, 3.2],
    spinYCandidates: [-0.5, -0.3, -0.15, 0, 0.1],
    positionScoreWeight: 2.0,
    safetyPowers: [1.0, 1.5, 2.0, 2.5],
    enableHidingSafety: true,
    forbidPreRailContact: true,
    forbidCaromPots: true,
  },
};

// ============================================================
// オーバーレイ（localStorage 永続化・cpuId 単位）
// ============================================================

const STORAGE_KEY = 'pool.cpu.config.v2';

/** key=cpuId（例 'cpu-efren'）, value=部分パッチ。 */
type Overrides = Record<string, Partial<StyleConfig>>;

const STYLE_KEYS_LOOKUP: Readonly<Record<string, boolean>> = {
  enableKicks: true,
  cutAngleHardLimit: true,
  farDistanceThreshold: true,
  farCutAngleLimit: true,
  powerCandidates: true,
  spinYCandidates: true,
  positionScoreWeight: true,
  safetyPowers: true,
  enableHidingSafety: true,
  forbidPreRailContact: true,
  forbidCaromPots: true,
};

const isStyleConfigKey = (k: string): k is keyof StyleConfig =>
  Object.prototype.hasOwnProperty.call(STYLE_KEYS_LOOKUP, k);

const sanitizeBool = (v: unknown): boolean | null =>
  typeof v === 'boolean' ? v : null;

const sanitizeNumber = (v: unknown): number | null => {
  if (typeof v !== 'number') return null;
  if (Number.isNaN(v)) return null;
  return v; // Infinity 可
};

const sanitizeNumberArray = (v: unknown): readonly number[] | null => {
  if (!Array.isArray(v)) return null;
  const arr: number[] = [];
  for (const x of v) {
    if (typeof x === 'number' && Number.isFinite(x)) arr.push(x);
  }
  return arr.length > 0 ? arr : null;
};

/** 任意の入力から StyleConfig 部分集合を安全に抽出する（不正値は黙って捨てる）。 */
const sanitizePatch = (
  fallbackStyle: CpuStyle,
  raw: unknown,
): Partial<StyleConfig> => {
  if (!raw || typeof raw !== 'object') return {};
  const defaults = STYLE_DEFAULTS[fallbackStyle] as unknown as Readonly<
    Record<string, unknown>
  >;
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(raw as Record<string, unknown>)) {
    if (!isStyleConfigKey(key)) continue;
    const def = defaults[key];
    if (typeof def === 'boolean') {
      const s = sanitizeBool(val);
      if (s !== null) out[key] = s;
    } else if (typeof def === 'number') {
      const s = sanitizeNumber(val);
      if (s !== null) out[key] = s;
    } else if (Array.isArray(def)) {
      const s = sanitizeNumberArray(val);
      if (s) out[key] = s;
    }
  }
  return out as Partial<StyleConfig>;
};

const loadOverrides = (): Overrides => {
  const raw = readJson<Overrides>(STORAGE_KEY);
  if (!raw || typeof raw !== 'object') return {};
  // cpuId ごとの patch を保持する（style 情報は持たないため、型妥当性のみゆるく検証）。
  const out: Overrides = {};
  for (const [cpuId, patch] of Object.entries(raw)) {
    if (typeof cpuId !== 'string' || cpuId.length === 0) continue;
    if (!patch || typeof patch !== 'object') continue;
    // sanitize はスタイル不明では実施できない（数値範囲はスタイル非依存だが
    // boolean/number/array の型判別はデフォルト参照に依存）。
    // → 取り急ぎ生のオブジェクトを保持し、getCpuConfig 時にスタイルを使って sanitize する。
    out[cpuId] = patch as Partial<StyleConfig>;
  }
  return out;
};

let _cache: Record<string, StyleConfig> | null = null;

const ensureCache = (): Record<string, StyleConfig> => {
  if (_cache) return _cache;
  // cache 構築時はオーバーレイの生データのみ取り込む。getCpuConfig 時に
  // fallbackStyle に応じてマージ＆sanitize する（cache は patch ベース）。
  // 本実装では取得関数側で merge するためここでは空辞書を返す（cache 不要）。
  _cache = {};
  return _cache;
};

/** 配列の浅い等価判定。 */
const arrayEqual = (a: readonly unknown[], b: readonly unknown[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
};

// ============================================================
// 公開 API（CPU プレイヤー個別・cpuId ベース）
// ============================================================

/**
 * 指定 CPU プレイヤーの実効設定（デフォルト + オーバーレイのマージ済み）を返す。
 *
 * @param cpuId CPU プレイヤー識別子（例 'cpu-efren'）
 * @param fallbackStyle そのプレイヤーのスタイル（オーバーレイが無いキーのフォールバック値に使う）
 */
export const getCpuConfig = (
  cpuId: string,
  fallbackStyle: CpuStyle,
): StyleConfig => {
  const overrides = loadOverrides();
  const raw = overrides[cpuId];
  const sanitized = raw ? sanitizePatch(fallbackStyle, raw) : {};
  return { ...STYLE_DEFAULTS[fallbackStyle], ...sanitized };
};

/** スタイル組込デフォルトを返す（参考用）。 */
export const getCpuConfigDefault = (style: CpuStyle): StyleConfig =>
  STYLE_DEFAULTS[style];

/** 指定 CPU プレイヤーの現在の永続化済みオーバーレイ（差分のみ）を返す。 */
export const getCpuOverrides = (
  cpuId: string,
  fallbackStyle: CpuStyle,
): Partial<StyleConfig> => {
  const overrides = loadOverrides();
  const raw = overrides[cpuId];
  return raw ? sanitizePatch(fallbackStyle, raw) : {};
};

/**
 * 指定 CPU プレイヤーの設定パッチをマージして永続化する。
 * - パッチ後の値がスタイルデフォルトと一致するキーは保存対象から除外。
 * - 全キーがデフォルトと一致した場合は overrides からそのプレイヤー自体を削除。
 */
export const setCpuConfig = (
  cpuId: string,
  fallbackStyle: CpuStyle,
  patch: Partial<StyleConfig>,
): void => {
  const overrides = loadOverrides();
  const sanitized = sanitizePatch(fallbackStyle, patch);
  const existing = overrides[cpuId]
    ? sanitizePatch(fallbackStyle, overrides[cpuId])
    : {};
  const merged: Record<string, unknown> = { ...existing, ...sanitized };
  const defaults = STYLE_DEFAULTS[fallbackStyle] as unknown as Readonly<
    Record<string, unknown>
  >;
  for (const key of Object.keys(merged)) {
    const v = merged[key];
    const d = defaults[key];
    const equal =
      Array.isArray(d) && Array.isArray(v)
        ? arrayEqual(d as readonly unknown[], v as readonly unknown[])
        : v === d;
    if (equal) delete merged[key];
  }
  if (Object.keys(merged).length === 0) {
    delete overrides[cpuId];
  } else {
    overrides[cpuId] = merged as Partial<StyleConfig>;
  }
  writeJson(STORAGE_KEY, overrides);
  _cache = null;
};

/** 指定 CPU プレイヤーのオーバーレイを全消去（スタイルデフォルトに戻す）。 */
export const resetCpuConfig = (cpuId: string): void => {
  const overrides = loadOverrides();
  delete overrides[cpuId];
  writeJson(STORAGE_KEY, overrides);
  _cache = null;
};

/** 全プレイヤーのオーバーレイを消去。 */
export const resetAllCpuConfig = (): void => {
  writeJson(STORAGE_KEY, {});
  _cache = null;
};

// ============================================================
// 管理 UI フォーム自動生成用メタデータ
// ============================================================

export type StyleFieldKind = 'number' | 'boolean' | 'number-array' | 'angle-deg';

export interface StyleFieldMeta {
  readonly key: keyof StyleConfig;
  readonly label: string;
  readonly kind: StyleFieldKind;
  /** number / angle-deg のスライダー範囲。 */
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  /** Infinity を許可するか（farDistanceThreshold 等）。空欄入力 → Infinity 扱い。 */
  readonly allowInfinity?: boolean;
  readonly hint?: string;
  /** number-array 種別で、チップ選択UIの候補値リスト。指定した値の中から複数選択する。 */
  readonly chipValues?: readonly number[];
}

export const STYLE_CONFIG_META: readonly StyleFieldMeta[] = [
  {
    key: 'enableKicks',
    label: 'キックショット候補生成',
    kind: 'boolean',
    hint: '【候補生成段階】1レール反射で的玉に当てる狙いを思考候補に含めるか（OFF=直撃のみ思考・計算量削減）',
  },
  {
    key: 'cutAngleHardLimit',
    label: 'カット角上限',
    kind: 'angle-deg',
    min: 0,
    max: 90,
    step: 1,
    hint: '超過候補は除外（90°=物理限界）',
  },
  {
    key: 'farDistanceThreshold',
    label: '遠距離判定 [m]',
    kind: 'number',
    min: 0.3,
    max: 3.0,
    step: 0.1,
    allowInfinity: true,
    hint: '空欄=∞（判定無効）',
  },
  {
    key: 'farCutAngleLimit',
    label: '遠距離カット角閾値',
    kind: 'angle-deg',
    min: 0,
    max: 90,
    step: 1,
  },
  {
    key: 'powerCandidates',
    label: 'パワー候補 [m/s]',
    kind: 'number-array',
    chipValues: [1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0, 5.5, 6.0],
    hint: '複数選択可。CPU はこの中から最良の1つを選ぶ。多いほど思考時間↑',
  },
  {
    key: 'spinYCandidates',
    label: 'スピンY候補',
    kind: 'number-array',
    chipValues: [-1.0, -0.7, -0.5, -0.3, -0.15, 0, 0.15, 0.3, 0.5, 0.7, 1.0],
    hint: '-=引き / 0=ストップ / +=押し（複数選択可）',
  },
  {
    key: 'positionScoreWeight',
    label: '位置スコア重み',
    kind: 'number',
    min: 0,
    max: 5.0,
    step: 0.1,
    hint: '大きいほど次球出しを重視',
  },
  {
    key: 'safetyPowers',
    label: 'セーフティパワー [m/s]',
    kind: 'number-array',
    chipValues: [0.5, 0.8, 1.0, 1.2, 1.5, 1.8, 2.2, 2.5, 3.0],
    hint: '⚠ 1.0 未満は短距離以外で「ノークッション反則」になりやすい。1.0〜2.5 推奨',
  },
  {
    key: 'enableHidingSafety',
    label: '隠しセーフティ評価',
    kind: 'boolean',
  },
  {
    key: 'forbidPreRailContact',
    label: 'キック軌道の評価上禁止',
    kind: 'boolean',
    hint: '【評価段階】シミュレーション結果で「手玉が的玉より先にレール接触」した候補を強制却下するか。ON=直撃以外は不採用（直撃不可なら別タイプの候補へ）／OFF=ペナルティのみで採用可能。上の「候補生成」と評価段階が違う（生成 ON + 評価 ON の組み合わせも意味あり：意図しないキック軌道だけ却下する）',
  },
  {
    key: 'forbidCaromPots',
    label: 'カロムポット禁止',
    kind: 'boolean',
  },
];

// ensureCache は内部関数。将来の最適化で使う想定。
void ensureCache;
